/**
 * Figma popular products tabs — Shopify custom element section.
 *
 * Liquid renders <figma-popular-products-tabs> and loads this file with `defer`, so the browser
 * upgrades the element and this class owns init / teardown. The `customElements.get` guard is
 * required: the section can be placed more than once on a page, which emits the same asset URL
 * twice, and a tag name cannot be redefined once registered.
 *
 * Behaviour:
 *  - Tab switching (keeps the matching collection link visible).
 *  - Per-panel "load more": shows `perPage` products initially; each click reveals `loadMoreStep` more
 *    until every product is shown, then the button flips to "View less" and collapses back to `perPage`.
 *    Collapsing animates the panel height and scrolls the section back into view, so the module (its
 *    heading / tab bar / first rows) never ends up hidden above the viewport. Expand and collapse use
 *    different durations / easings (collapse is slower, ease-in-out) and newly revealed cards fade in
 *    with a small stagger, driven by the `data-revealing` CSS in figma-popular-products-tabs.css.
 */
class FigmaPopularProductsTabs extends HTMLElement {
  connectedCallback() {
    // Moving an already upgraded node within the document re-runs connectedCallback.
    if (this.dataset.popularReady) {
      return;
    }

    this.dataset.popularReady = 'true';

    this.perPage = parseInt(this.dataset.perPage, 10);
    if (!this.perPage || this.perPage < 1) {
      this.perPage = 4;
    }

    this.loadMoreStep = parseInt(this.dataset.loadMoreStep, 10);
    if (!this.loadMoreStep || this.loadMoreStep < 1) {
      this.loadMoreStep = this.perPage;
    }

    this.tabs = Array.from(this.querySelectorAll('[role="tab"][data-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));
    this.collectionLinks = Array.from(
      this.querySelectorAll('[data-tab-view-more], [data-tab-mobile-view-more]')
    );

    this.handleKeydown = (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
        return;
      }

      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = (this.tabs.indexOf(event.currentTarget) + direction + this.tabs.length) % this.tabs.length;
      this.tabs[nextIndex].focus();
      this.activateTab(this.tabs[nextIndex]);
    };

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activateTab(tab));
      tab.addEventListener('keydown', this.handleKeydown);
    });

    this.panels.forEach((panel) => this.initLoadMore(panel));
  }

  disconnectedCallback() {
    if (this.handleKeydown) {
      this.tabs.forEach((tab) => tab.removeEventListener('keydown', this.handleKeydown));
      this.handleKeydown = null;
    }

    this.panels.forEach((panel) => {
      const button = panel.querySelector('[data-load-more]');
      if (button && button._loadMoreHandler) {
        button.removeEventListener('click', button._loadMoreHandler);
        button._loadMoreHandler = null;
      }
    });
  }

  activateTab(tab) {
    const key = tab.dataset.tab;

    this.tabs.forEach((item) => {
      item.setAttribute('aria-selected', item === tab ? 'true' : 'false');
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.tabPanel !== key;
    });

    this.collectionLinks.forEach((link) => {
      const forTab = link.dataset.tabViewMore || link.dataset.tabMobileViewMore;
      link.hidden = forTab !== key;
    });
  }

  initLoadMore(panel) {
    const button = panel.querySelector('[data-load-more]');
    if (!button) {
      return;
    }

    const cards = Array.from(panel.querySelectorAll('[data-popular-item]'));
    if (cards.length <= this.perPage) {
      button.hidden = true;
      return;
    }

    const label = button.querySelector('[data-load-more-label]');
    const arrow = button.querySelector('[data-load-more-arrow]');
    let visible = this.perPage;

    const render = () => {
      const expanded = visible >= cards.length;

      cards.forEach((card, index) => {
        card.hidden = index >= visible;
      });

      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (label) {
        label.textContent = expanded ? button.dataset.loadLessLabel : button.dataset.loadMoreLabel;
      }
      if (arrow) {
        arrow.setAttribute('data-expanded', expanded ? 'true' : 'false');
      }
    };

    let revealTimer = 0;
    const clearReveals = () => {
      clearTimeout(revealTimer);
      cards.forEach((card) => card.removeAttribute('data-revealing'));
    };

    button._loadMoreHandler = () => {
      const collapsing = visible >= cards.length;
      const previousVisible = visible;

      clearReveals();

      if (collapsing) {
        // Two-phase collapse: the cards that are leaving first dissolve in place
        // (kept rendered and faded via CSS), then the panel height reels in. Nothing
        // vanishes abruptly.
        const hiding = cards.slice(this.perPage);
        this.animatePanelHeight(
          panel,
          () => {
            // Everything is already shown — collapse back to the initial `perPage`.
            visible = this.perPage;
            render();
          },
          collapsing,
          () => {
            // After the end height was measured, restore the leaving cards so they
            // can fade out; hide them for real once the dissolve has finished.
            hiding.forEach((card) => {
              card.hidden = false;
              card.classList.add('figma-popular-tabs__card--fading');
            });
            panel._collapseHideTimer = setTimeout(() => {
              panel._collapseHideTimer = 0;
              hiding.forEach((card) => {
                card.hidden = true;
                card.classList.remove('figma-popular-tabs__card--fading');
              });
            }, 300);
          }
        );

        // Collapsing shortens the page, which can leave the whole section above the
        // viewport. Bring it back so the heading / tab bar / first rows stay visible.
        this.scrollSectionIntoView();
      } else {
        // Animate the panel height around the state change so the grid grows
        // smoothly instead of jumping.
        this.animatePanelHeight(
          panel,
          () => {
            // Reveal `loadMoreStep` more until all products are visible (never skips the last one).
            visible = Math.min(visible + this.loadMoreStep, cards.length);
            render();
          },
          collapsing
        );

        // Soft staggered fade-in on the cards that just appeared.
        let revealIndex = 0;
        cards.forEach((card, index) => {
          if (index >= previousVisible && index < visible) {
            card.style.setProperty('--reveal-index', String(revealIndex));
            card.setAttribute('data-revealing', '');
            revealIndex += 1;
          }
        });
        revealTimer = setTimeout(clearReveals, 1000);
      }
    };

    button.addEventListener('click', button._loadMoreHandler);
    render();
  }

  animatePanelHeight(panel, mutate, collapsing, postMeasure) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Cancel any in-flight animation first so both height measurements are natural.
    if (panel._heightAnimationFinish) {
      panel._heightAnimationFinish();
    }
    if (panel._collapseHideTimer) {
      clearTimeout(panel._collapseHideTimer);
      panel._collapseHideTimer = 0;
    }
    panel.querySelectorAll('.figma-popular-tabs__card--fading').forEach((card) => {
      card.classList.remove('figma-popular-tabs__card--fading');
    });

    const startHeight = panel.offsetHeight;
    mutate();
    const endHeight = panel.offsetHeight;

    if (reduceMotion || startHeight === endHeight) {
      return;
    }

    // Collapse is deliberately slower with an ease-in-out curve so the list reels in
    // gradually; expand is quicker with a fast-start / gentle-settle curve.
    const duration = collapsing ? 680 : 480;
    const easing = collapsing ? 'cubic-bezier(0.45, 0, 0.2, 1)' : 'cubic-bezier(0.22, 1, 0.36, 1)';

    let timer = 0;
    const finish = () => {
      panel.style.height = '';
      panel.style.overflow = '';
      panel.style.transition = '';
      panel.removeEventListener('transitionend', onEnd);
      clearTimeout(timer);
      if (panel._collapseHideTimer) {
        clearTimeout(panel._collapseHideTimer);
        panel._collapseHideTimer = 0;
      }
      delete panel._heightAnimationFinish;
    };
    const onEnd = (event) => {
      if (event.target === panel && event.propertyName === 'height') {
        finish();
      }
    };

    timer = setTimeout(finish, duration + 150);

    panel.style.height = startHeight + 'px';
    panel.style.overflow = 'hidden';
    if (postMeasure) {
      // Runs while the start height is already pinned, before the transition begins,
      // so restoring the leaving cards here causes no visual jump.
      postMeasure();
    }
    panel.getBoundingClientRect(); // commit the start height before transitioning
    panel.style.transition = 'height ' + duration + 'ms ' + easing;
    panel.style.height = endHeight + 'px';

    panel._heightAnimationFinish = finish;
    panel.addEventListener('transitionend', onEnd);
  }

  scrollSectionIntoView() {
    if (this.getBoundingClientRect().top >= 0) {
      // The section start (heading / tabs) is still on screen — nothing to do.
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }
}

if (!customElements.get('figma-popular-products-tabs')) {
  customElements.define('figma-popular-products-tabs', FigmaPopularProductsTabs);
}
